/**
 * 자동 i18n 라우팅 레이아웃 - 공유 라이브러리 버전
 * Optional Segments 패턴으로 언어가 없는 경우 자동으로 현재 언어로 리다이렉트
 * React Router v7 SPA 모드 호환
 */ import React, { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useParams, useLocation } from 'react-router';
import { setLocale } from '@paraglide/runtime';
import { LanguageService } from '@edusense/shared-i18n';
/**
 * AutoI18nLayout - 자동 언어 라우팅 처리
 * 
 * Optional Segments 패턴 (:lang?) 사용:
 * - /feed → /ko/feed (자동 리다이렉트)
 * - /ko/feed → 그대로 렌더링
 * - /en/feed → 그대로 렌더링
 * 
 * 장점:
 * - 컴포넌트에서 언어 prefix 수동 추가 불필요
 * - 라우터 설정 수준에서 완전 자동화
 * - SPA 모드에서 완벽 동작
 * - React Router v7 타이밍 이슈 자동 해결
 */ export var AutoI18nLayout = function(param) {
    var _param_debug = param.debug, debug = _param_debug === void 0 ? false : _param_debug, defaultLanguage = param.defaultLanguage, _param_isValidLanguage = param.isValidLanguage, isValidLanguage = _param_isValidLanguage === void 0 ? function(lang) {
        return LanguageService.isValidLanguage(lang);
    } : _param_isValidLanguage;
    // Router hooks를 무조건 호출 (Hook Rules 준수)
    var params = useParams();
    var location = useLocation();
    // URL에서 현재 언어 추출
    var urlLang = params.lang;
    // 디버그 로그
    if (debug) {
        console.log('🔄🔄🔄 [AutoI18nLayout] Render:', {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            urlLang: urlLang,
            params: Object.keys(params).length > 0 ? params : 'EMPTY PARAMS',
            allParams: params
        });
    }
    // Force location tracking to detect if hooks are updating
    useEffect(function() {
        if (debug) {
            console.log('🚨🚨🚨 [AutoI18nLayout] Location Effect Triggered:', {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                params: params,
                urlLang: urlLang,
                timestamp: new Date().toISOString(),
                '🔍 Effect Dependency': 'location.pathname changed'
            });
        }
    }, [
        location.pathname,
        location.search,
        location.hash,
        debug,
        params,
        urlLang
    ]);
    // useEffect로 언어 저장 및 Paraglide 동기화
    useEffect(function() {
        if (urlLang && isValidLanguage(urlLang)) {
            if (debug) {
                console.log("\uD83D\uDD04 [AutoI18nLayout] Page Change Effect: ".concat(location.pathname, " (lang: ").concat(urlLang, ")"));
            }
            // localStorage에 언어 저장
            LanguageService.saveLanguage(urlLang);
            if (debug) {
                console.log("\uD83C\uDF10 [AutoI18nLayout] Language ".concat(urlLang, " saved to localStorage"));
            }
            // Paraglide locale 동기화
            try {
                setLocale(urlLang, {
                    reload: false
                });
                if (debug) {
                    console.log("\uD83D\uDE80 [AutoI18nLayout] Paraglide locale updated to: ".concat(urlLang));
                }
            } catch (error) {
                console.warn("⚠️ [AutoI18nLayout] Paraglide setLocale failed:", error);
            }
        }
    }, [
        urlLang,
        location.pathname,
        debug,
        isValidLanguage
    ]);
    // 유효하지 않은 언어 처리를 포함한 안전한 리다이렉트 처리
    var redirectInfo = useMemo(function() {
        if (debug) {
            console.log("\uD83D\uDD04\uD83D\uDD04\uD83D\uDD04 [AutoI18nLayout] Memo Recalc:", {
                urlLang: urlLang || 'NONE',
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                isValidLang: urlLang ? isValidLanguage(urlLang) : false
            });
        }
        // 언어가 없거나 유효하지 않은 언어인 경우 리다이렉트 필요
        if (!urlLang || !isValidLanguage(urlLang)) {
            // 현재 언어 결정 (localStorage > 브라우저 > 기본값)
            var currentLang = defaultLanguage || LanguageService.getDefaultLanguage();
            if (debug) {
                console.log('🔍 [AutoI18nLayout] Language detection or invalid lang redirect:', {
                    urlLang: urlLang || 'NONE',
                    isValidLang: urlLang ? isValidLanguage(urlLang) : false,
                    currentLang: currentLang,
                    isValid: isValidLanguage(currentLang),
                    localStorage: typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') : 'N/A'
                });
            }
            // 유효하지 않은 언어가 경로에 있다면 제거하고 올바른 언어 추가
            var pathWithoutLang;
            if (urlLang && !isValidLanguage(urlLang)) {
                // 유효하지 않은 언어(예: 'feed')를 실제 경로로 처리
                pathWithoutLang = location.pathname; // /feed를 그대로 유지
                if (debug) {
                    console.log("\uD83D\uDEA8 [AutoI18nLayout] Invalid language detected: ".concat(urlLang, ", treating as path: ").concat(pathWithoutLang));
                }
            } else {
                // 언어가 없는 경우 일반적인 처리
                pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
            }
            var redirectTo = "/".concat(currentLang).concat(pathWithoutLang).concat(location.search).concat(location.hash);
            if (debug) {
                console.log("\uD83D\uDD00\uD83D\uDD00\uD83D\uDD00 [AutoI18nLayout] Auto I18n Redirect: ".concat(location.pathname, " → ").concat(redirectTo, " (lang: ").concat(currentLang, ")"));
                console.log('🚀 [AutoI18nLayout] About to Navigate to:', redirectTo);
            }
            return {
                needsRedirect: true,
                redirectTo: redirectTo
            };
        }
        return {
            needsRedirect: false,
            redirectTo: null
        };
    }, [
        urlLang,
        location.pathname,
        location.search,
        location.hash,
        debug,
        isValidLanguage,
        defaultLanguage
    ]);
    // 리다이렉트 필요시에만 Navigate 반환
    if (redirectInfo.needsRedirect) {
        return /*#__PURE__*/ React.createElement(Navigate, {
            to: redirectInfo.redirectTo,
            replace: true
        });
    }
    // 언어가 있는 경우 정상 렌더링  
    if (debug) {
        console.log("✅ [AutoI18nLayout] Language found: ".concat(urlLang, ", rendering children"));
        console.log("✅ [AutoI18nLayout] Render: Using key={location.pathname} for forced remount");
    }
    // React Router v7 - key 없이 Outlet 반환 (자연스러운 reconciliation)
    return /*#__PURE__*/ React.createElement(Outlet, null);
};
export default AutoI18nLayout;
